import i18n from "@/lib/i18n";
import { getDateLocale } from "@/lib/dateFormat";
import type { Meeting, Summary, Transcript } from "@/types";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString(getDateLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "";
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return sec > 0
    ? i18n.t("pdf.durationMinSec", { min, sec })
    : i18n.t("pdf.durationMin", { min });
}

export function buildHtml(
  meeting: Meeting,
  summary: Summary,
  transcript?: Transcript
): string {
  const t = i18n.t.bind(i18n);
  const content = summary.content;
  const title = escapeHtml(
    content.title || meeting.title || i18n.t("meetings.untitled")
  );
  const date = formatDate(meeting.recorded_at);
  const duration = formatDuration(meeting.duration_seconds);
  const lang = i18n.language;

  const contextHtml = meeting.context
    ? `<div class="overview" style="border-left-color: #A0A0B0;">
        <p style="color: #666; font-size: 13px;"><strong>${t("pdf.context")}</strong> ${escapeHtml(meeting.context)}</p>
      </div>`
    : "";

  const participantsHtml =
    content.participants.length > 0
      ? `<div class="section">
          <h2>${t("pdf.participants")}</h2>
          <p>${content.participants.map(escapeHtml).join(", ")}</p>
        </div>`
      : "";

  const decisionsHtml =
    content.decisions.length > 0
      ? `<div class="section">
          <h2>${t("pdf.decisions")}</h2>
          <ul>${content.decisions.map((d) => `<li>${escapeHtml(d)}</li>`).join("")}</ul>
        </div>`
      : "";

  const actionsHtml =
    content.actions.length > 0
      ? `<div class="section">
          <h2>${t("pdf.actions")}</h2>
          <table>
            <thead><tr><th>${t("pdf.responsible")}</th><th>${t("pdf.action")}</th><th>${t("pdf.deadline")}</th></tr></thead>
            <tbody>${content.actions
              .map(
                (a) =>
                  `<tr><td>${escapeHtml(a.who)}</td><td>${escapeHtml(a.what)}</td><td>${escapeHtml(a.deadline)}</td></tr>`
              )
              .join("")}</tbody>
          </table>
        </div>`
      : "";

  const nextStepsHtml =
    content.next_steps.length > 0
      ? `<div class="section">
          <h2>${t("pdf.nextSteps")}</h2>
          <ul>${content.next_steps.map((s) => `<li>${escapeHtml(s)}</li>`).join("")}</ul>
        </div>`
      : "";

  const quotesHtml =
    content.key_quotes && content.key_quotes.length > 0
      ? `<div class="section">
          <h2>${t("pdf.keyQuotes")}</h2>
          ${content.key_quotes.map((q) => `<blockquote>"${escapeHtml(q)}"</blockquote>`).join("")}
        </div>`
      : "";

  const transcriptText = transcript ? (transcript.edited_text ?? transcript.raw_text) : null;
  const transcriptHtml = transcriptText
    ? `<div class="section transcript">
        <h2>${t("pdf.fullTranscript")}</h2>
        <p>${escapeHtml(transcriptText).replace(/\n/g, "<br/>")}</p>
      </div>`
    : "";

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8"/>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1a1a1a;
      padding: 40px;
      font-size: 14px;
      line-height: 1.6;
    }
    .header {
      border-bottom: 3px solid #6C63FF;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .header .brand {
      color: #6C63FF;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 8px;
    }
    .header h1 {
      color: #1a1a1a;
      font-size: 22px;
      font-weight: 700;
      margin-bottom: 4px;
    }
    .header .meta {
      color: #666;
      font-size: 13px;
    }
    .section {
      margin-bottom: 20px;
    }
    h2 {
      color: #6C63FF;
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #e0e0e0;
    }
    .overview {
      background: #f8f8ff;
      border-left: 4px solid #6C63FF;
      padding: 12px 16px;
      border-radius: 0 4px 4px 0;
      margin-bottom: 20px;
    }
    ul { padding-left: 20px; }
    li { margin-bottom: 4px; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    th, td { text-align: left; padding: 8px 12px; border-bottom: 1px solid #e0e0e0; }
    th { background: #f5f5ff; font-weight: 600; font-size: 13px; color: #444; }
    blockquote { border-left: 3px solid #ddd; padding: 8px 16px; color: #555; font-style: italic; margin-bottom: 8px; }
    .transcript { page-break-before: always; }
    .transcript p { font-size: 13px; color: #333; }
    .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e0e0e0; text-align: center; color: #999; font-size: 11px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">${t("pdf.brand")}</div>
    <h1>${title}</h1>
    <div class="meta">${date}${duration ? ` · ${duration}` : ""}</div>
  </div>

  ${contextHtml}
  <div class="overview">
    <p>${escapeHtml(content.overview)}</p>
  </div>

  ${participantsHtml}
  ${decisionsHtml}
  ${actionsHtml}
  ${nextStepsHtml}
  ${quotesHtml}
  ${transcriptHtml}

  <div class="footer">${t("pdf.footer")}</div>
</body>
</html>`;
}

export async function generateAndDownloadPdf(
  meeting: Meeting,
  summary: Summary,
  transcript?: Transcript
): Promise<void> {
  const html2pdf = (await import("html2pdf.js")).default;

  const htmlContent = buildHtml(meeting, summary, transcript);

  const container = document.createElement("div");
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  const title =
    summary.content.title || meeting.title || i18n.t("meetings.untitled");

  await html2pdf()
    .set({
      margin: [15, 15, 15, 15],
      filename: `${title}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .from(container)
    .save();

  document.body.removeChild(container);
}
